import React, { useState } from "react";
import { IDriver } from "../models/Driver";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { DriverService } from "../services/DriverService";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { Toast } from "../../../Shared/components/Toast";
import { UtilService } from "../../../Shared/Utils/UtilService";
import { AuthService } from "../../Auth/services/AuthService";
import { GenericZoomFile } from "../../../Shared/components/GenericZoomFile";
import { DriverForm } from "./DriverForm";
import { GenericButton } from "../../../Shared/components/GenericButton";

interface DriverDetailProps {
  driver?: IDriver;
}

const XDiv = ({
  label,
  value,
}: {
  label: string;
  value: string | number | undefined;
}) => (
  <div>
    <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
    <p className="font-medium text-gray-900 dark:text-white">{value || "—"}</p>
  </div>
);

const formatDate = (date: string) => new Date(date).toLocaleDateString();

export const Driver: React.FC<DriverDetailProps> = () => {
  const message = "Loading Driver Data, Please Wait";

  const [searchParams] = useSearchParams();
  const driverId = searchParams.get("driver");

  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();

  const findDriver = async () => {
    try {
      setIsLoading(true);
      return await DriverService.getById(driverId ?? "");
    } catch (error: any) {
      setIsLoading(false);
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const query = useQuery({
    queryKey: ["driver"],
    queryFn: findDriver,
    placeholderData: (previous) => previous?.data,
  });

  let isAllowed = AuthService.isManagementAllowed();

  const [isDriverModalOpen, setIsDriverModalOpen] = useState(false);
  const toggleDriverModal = (success: boolean) => {
    setIsDriverModalOpen(!isDriverModalOpen);
    if (success) {
      query.refetch();
    }
  };
  const onAddDriverAction = () => setIsDriverModalOpen(!isDriverModalOpen);

  return (
    <>
      {isDriverModalOpen && (
        <DriverForm onClose={toggleDriverModal} driver={query.data!} />
      )}
      <div className="px-5 mx-5 pb-10 mb-10">
        <div className="px-6 bg-white dark:bg-gray-800 rounded-xl shadow-xxl mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
            <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">
              {UtilService.titleCase(query.data?.firstName ?? "")}{" "}
              {UtilService.titleCase(query.data?.middleName ?? "")}{" "}
              {UtilService.titleCase(query.data?.lastName ?? "")}
            </h2>

            <div className="flex">
              {!isAllowed && (
                <GenericButton
                  isFull={false}
                  type="button"
                  onClick={onAddDriverAction}
                >
                  Update
                </GenericButton>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <XDiv label="Country/Address" value={query.data?.address} />
            <XDiv label="Phone Number" value={query.data?.phoneNumber} />
            <XDiv label="Email Address" value={query.data?.email} />

            <XDiv
              label="Created Date"
              value={`${formatDate(query.data?.createdAt)}`}
            />

            <XDiv label="Company Name" value={query.data?.company?.name} />
            <XDiv
              label="Company Address"
              value={query.data?.company?.address}
            />

            <div className="col-span-1 md:col-span-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                Driver Licenses
              </label>
              <div className="list-disc list-inside flex flex-row gap-4 text-gray-600 dark:text-gray-400">
                {query.data?.licenses?.map((attach: any, index: any) => (
                  <div key={index}>
                    <GenericZoomFile src={attach} height={100} width={100} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="">
            <br />
            <hr />
            <br />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <XDiv
              label="Vehicle Information"
              value={query.data?.vehicle?.name}
            />
            <XDiv
              label="Vehicle Chassis Number"
              value={query.data?.vehicle?.chassisNumber}
            />
            <XDiv
              label="Vehicle Plate Number"
              value={query.data?.vehicle?.plateNumber}
            />
            <div className="col-span-1 md:col-span-2">
              <Link
                to={`/services/vehicles/details/?vehicle=${query.data?.vehicle?.id}`}
                className="text-blue-600 hover:underline"
              >
                View Vehicle Details
              </Link>
            </div>
          </div>
          {showToast && (
            <Toast
              type={toast?.type}
              message={toast?.message ?? ""}
              onClose={() => setShowToast(false)}
            />
          )}
          {isLoading && <OverlayLoader message={message} />}
        </div>
      </div>
    </>
  );
};
